import { createSelector } from '@ngrx/store';
import * as RA from 'ramda-adjunct';
import { getManagementState, ManagementState } from '../management.reducer';
import { adapter, LocationSetTypesState } from './location-set-types.reducer';
import {asyncActionState} from '@emm/core/util/async-action-state';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {SearchFilter} from '@emm/core/models/search-filter';

const getState = createSelector(
    getManagementState,
    (state: ManagementState) => state.locationSetTypes
);

const entitySelectors = adapter.getSelectors(getState);
const asyncActionStateSelectors = asyncActionState.getSelectors(getState);

const getSelectedTag = createSelector(getState, (state: LocationSetTypesState) => state.selectedTag);
const getSelected = createSelector(
    entitySelectors.selectEntities,
    getSelectedTag,
    (entities, selectedTag) => selectedTag && entities[selectedTag]
);
const getValidAll = createSelector(
    entitySelectors.selectAll,
    asyncActionStateSelectors.getHasError,
    asyncActionStateSelectors.getSuccess,
    (items, hasError, success) => (!hasError && success ? items : null)
);
const getSearchFilter = createSelector(getState, (state: LocationSetTypesState) => state.searchFilter);
const getFilteredLocationSetTypes = createSelector(
    getValidAll,
    getSearchFilter,
    (locationSetTypes: LocationSetType[], filter: SearchFilter) => {
        const useFilter =
            RA.isNonEmptyArray(locationSetTypes) &&
            RA.isNotNilOrEmpty(filter) &&
            RA.isNonEmptyString(filter.key) &&
            RA.isNotNil(filter.value);
        const filterString = (filter?.value ? filter.value.toString().toLowerCase() : []);

        const filteredItems = locationSetTypes?.filter((item: LocationSetType) => {
            if (filter?.key === 'weight') {
                return item[filter?.key].toString() === filterString;
            } else {
                return item[filter?.key] && item[filter?.key].toLowerCase().includes(filterString);
            }
        });

        return useFilter ? filteredItems : locationSetTypes;
    }
);

const getOnlyParents = createSelector(entitySelectors.selectAll, (items) =>
    items.filter((item) => item.parentScope === null)
);

const getValidOnlyParents = createSelector(
    getOnlyParents,
    asyncActionStateSelectors.getHasError,
    asyncActionStateSelectors.getSuccess,
    (items, hasError, success) => (!hasError && success ? items : null)
);

const getIsLocationSetTypeParent = createSelector(
    getSelected,
    (selected) => RA.isNotNull(selected) && RA.isNotNull(selected.childScope)
);

const getDisplayResultsImmediately = createSelector(
    getSelected,
    (selected) => RA.isNonEmptyString(selected?.tag) && selected?.tag !== 'LOCATION'
);

export const LocationSetTypesSelectors = {
    entitySelectors,
    ...asyncActionStateSelectors,
    getSelectedTag,
    getSelected,
    getValidAll,
    getSearchFilter,
    getFilteredLocationSetTypes,
    getOnlyParents,
    getValidOnlyParents,
    getIsLocationSetTypeParent,
    getDisplayResultsImmediately
};
